import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { Box, CollapsibleSection, Loader } from '@/common/components';
import { Variants } from '@/common/types';
import { BLANK_VALUE, GENETIC_COUNSELING_TRANSLATION } from '@/modules/genetic-counseling/constants';
import { ProfileHistoryProp } from './ProfileHistory.types';
import { CHANGE_TYPE, HISTORY_EVENTS, PatientHistory } from '@/modules/genetic-counseling/types';
import {
  StyledHistoryContainer,
  StyledHistoryDetails,
  StyledHistoryEvent,
  StyledHistoryRow,
  StyledNoDataAvailable,
} from './ProfileHistory.styles';
import { getAppointmentTime } from '@modules/genetic-counseling/utils';
import { formatDate } from '@common/utils';

const ProfileHistory: FC<ProfileHistoryProp> = ({ data, isLoading }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);

  const getTime = (dataStartTime: string, dataEndTime: string, dataTimeZone: string): string => {
    const startTime = dataStartTime;
    const endTime = dataEndTime;
    const timezone = dataTimeZone;
    if (!startTime || !endTime || !timezone) {
      return '';
    }

    return getAppointmentTime(startTime, endTime, timezone) ?? '';
  };

  const getFormattedDate = (date: string | undefined): string => {
    return date ? formatDate(date) : BLANK_VALUE;
  };

  const renderEvent = (history: PatientHistory): ReactNode => {
    const currentAppointmentTime = getTime(
      history.curAppointmentStartTime,
      history.curAppointmentEndTime,
      history.curAppointmentTimezone,
    );
    const currentAppointmentDate = getFormattedDate(history.curAppointmentStartTime);
    const previousAppointmentDate = getFormattedDate(history.prevAppointmentStartTime);
    const previousAppointmentTime = getTime(
      history.prevAppointmentStartTime,
      history.prevAppointmentEndTime,
      history.prevAppointmentTimezone,
    );
    switch (history.event) {
      case HISTORY_EVENTS.UPDATE:
        if (history.changeType === CHANGE_TYPE.EMAIL) {
          return (
            <StyledHistoryEvent data-testid={'profile-history-event-email'}>
              {t('profile_history.update.email', { prevChange: history.prevChange, curChange: history.curChange })}
            </StyledHistoryEvent>
          );
        }

        if (history.changeType === CHANGE_TYPE.PHONE) {
          return (
            <StyledHistoryEvent data-testid={'profile-history-event-phone'}>
              {t('profile_history.update.phone', { prevChange: history.prevChange, curChange: history.curChange })}
            </StyledHistoryEvent>
          );
        }
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-phone'}>
            {t('profile_history.update.default')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.COMPLETED:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-completed'}>
            {t('profile_history.complete', {
              curStartDate: format(new Date(history.curAppointmentStartTime), "MMMM dd, yyyy 'at' H:mmaaa"),
              curEndDate: format(new Date(history.curAppointmentEndTime), 'H:mmaaa'),
            })}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.VIEWED:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-viewed'}>
            {t('profile_history.view')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.RESCHEDULE:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-reschedule'}>
            {t('profile_history.reschedule', {
              curStartDate: currentAppointmentDate,
              curStartTime: currentAppointmentTime,
              prevStartDate: previousAppointmentDate,
              prevStartTime: previousAppointmentTime,
            })}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.SCHEDULE:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-schedule'}>
            {t('profile_history.schedule', {
              curStartDate: currentAppointmentDate,
              curStartTime: currentAppointmentTime,
            })}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.CREATE:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-create'}>
            {t('profile_history.create')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.ATTEMPTED:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-attempted'}>
            {t('profile_history.attempted')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.DECLINE:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-decline'}>
            {t('profile_history.decline', { reason: history.declineReasonCode })}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.CANCEL:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-cancel'}>
            {t('profile_history.cancel')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.REOPEN:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-reopen'}>
            {t('profile_history.reopen')}
          </StyledHistoryEvent>
        );
      case HISTORY_EVENTS.MANUAL_CREATE:
        return (
          <StyledHistoryEvent data-testid={'profile-history-event-manual-create'}>
            {t('profile_history.manual_create')}
          </StyledHistoryEvent>
        );
      default:
        break;
    }
  };

  const renderDetails = ({ eventTimestamp, performer }: PatientHistory): ReactNode => {
    return (
      <StyledHistoryDetails>
        <Box data-testid={'profile-history-date'}>{format(new Date(eventTimestamp), "MMMM dd, yyyy 'at' H:mmaaa")}</Box>
        {performer && <Box data-testid={'profile-history-by'}>{`${t('patient_profile.by')} ${performer}`}</Box>}
      </StyledHistoryDetails>
    );
  };

  // Filter viewed event from history
  data = data?.filter((item) => item.event !== HISTORY_EVENTS.VIEWED);

  return (
    <CollapsibleSection title={t('patient_profile.profile_history')} variant={Variants.SECONDARY}>
      <StyledHistoryContainer data-testid={'profile-history'}>
        {isLoading && <Loader data-testid={'profile-history-loader'} css={{ height: '80px' }} />}
        {data && data.length > 0 && !isLoading && (
          <>
            {data.map((history, index) => (
              <StyledHistoryRow key={index} data-testid={`profile-history-row-${index}`}>
                <Box data-testid={`profile-history-event-${index}`}>{renderEvent(history)}</Box>
                <Box data-testid={`profile-history-details-${index}`}>{renderDetails(history)}</Box>
              </StyledHistoryRow>
            ))}
          </>
        )}
        {(!data || data === null || data.length === 0) && !isLoading && (
          <StyledNoDataAvailable data-testid={'profile-history-no-data'}>
            {t('profile_history.no_data')}
          </StyledNoDataAvailable>
        )}
      </StyledHistoryContainer>
    </CollapsibleSection>
  );
};

export default ProfileHistory;
